import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, UsersRound, Send, Play, FastForward, CheckCircle2, History, MessageSquare, AlertTriangle, Sparkles, RefreshCw, Users, Star } from 'lucide-react';
import type { Turma, SendProgress, WhatsAppLog, WhatsAppBotConfig } from '@/domains/whatsappBot';
import { whatsappBotService } from '@/domains/whatsappBot';
import BulkImportTab from './BulkImportTab';
import AiMessageGenerator from './AiMessageGenerator';
import BotGroupManager from './BotGroupManager';
import { toast } from 'sonner';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

interface BotCampaignsProps {
  turmas: Turma[];
  loadingTurmas: boolean;
  onRefreshTurmas: () => void;
  isConnected: boolean;
  escolaId: string;
  config: WhatsAppBotConfig | null;
  savingConfig: boolean;
  onSaveConfig: (cfg: Partial<WhatsAppBotConfig>) => Promise<void>;
  
  // States to send
  onSendToGroup: (turmaId: string, message: string) => Promise<void>;
  sendingGroup: boolean;
  sendProgress: SendProgress | null;
  
  // Logs & History
  logs: WhatsAppLog[];
  loadingLogs: boolean;
  onRefreshLogs: () => void;
}

export default function BotCampaigns({
  turmas,
  loadingTurmas,
  onRefreshTurmas,
  isConnected,
  escolaId,
  config,
  savingConfig,
  onSaveConfig,
  onSendToGroup,
  sendingGroup,
  sendProgress,
  logs,
  loadingLogs,
  onRefreshLogs
}: BotCampaignsProps) {
  const [activeTab, setActiveTab] = useState<'turmas' | 'groups' | 'import' | 'history'>('turmas');
  const [selectedTurmaId, setSelectedTurmaId] = useState<string>('');
  const [campaignMessage, setCampaignMessage] = useState('');
  const [confirmCampaignOpen, setConfirmCampaignOpen] = useState(false);

  // Group Dispatch State
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [groupMessage, setGroupMessage] = useState('');
  const [sendingToGroup, setSendingToGroup] = useState(false);

  const favoriteGroups = config?.grupos_favoritos || [];
  const selectedGroup = favoriteGroups.find(g => g.id === selectedGroupId);

  const handleSendToWAGroup = async () => {
    if (!selectedGroupId || !groupMessage.trim()) return;
    setSendingToGroup(true);
    try {
      await whatsappBotService.sendToWhatsAppGroup(escolaId, selectedGroupId, groupMessage);
      toast.success('Mensagem enviada ao grupo com sucesso!');
      setGroupMessage('');
    } catch (err: any) {
      toast.error('Erro ao enviar para o grupo: ' + err.message);
    } finally {
      setSendingToGroup(false);
    }
  };

  const selectedTurma = turmas.find(t => t.id === selectedTurmaId);

  const handleStartCampaign = async () => {
    if (!selectedTurmaId || !campaignMessage.trim()) return;
    setConfirmCampaignOpen(false);
    await onSendToGroup(selectedTurmaId, campaignMessage);
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
      
      {/* Sidebar / Navegação de Campanhas */}
      <div className="flex gap-2 overflow-x-auto pb-1 lg:col-span-1 lg:block lg:space-y-2 lg:overflow-visible">
        <Button 
            variant={activeTab === 'turmas' ? 'default' : 'ghost'} 
            className={`shrink-0 justify-start lg:w-full ${activeTab === 'turmas' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
            onClick={() => setActiveTab('turmas')}
        >
          <UsersRound className="mr-2 h-4 w-4" /> Disparo p/ Turmas
        </Button>
        <Button 
            variant={activeTab === 'groups' ? 'default' : 'ghost'} 
            className={`shrink-0 justify-start lg:w-full ${activeTab === 'groups' ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}`}
            onClick={() => setActiveTab('groups')}
        >
          <Star className="mr-2 h-4 w-4" /> Disparo p/ Grupos
          {favoriteGroups.length > 0 && (
            <Badge className="ml-auto bg-amber-100 text-xs text-amber-800 hover:bg-amber-100">{favoriteGroups.length}</Badge>
          )}
        </Button>
        <Button 
            variant={activeTab === 'import' ? 'default' : 'ghost'} 
            className={`shrink-0 justify-start lg:w-full ${activeTab === 'import' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
            onClick={() => setActiveTab('import')}
        >
          <Send className="mr-2 h-4 w-4" /> Importação em Massa (Excel)
        </Button>
        <Button 
            variant={activeTab === 'history' ? 'default' : 'ghost'} 
            className={`shrink-0 justify-start lg:w-full ${activeTab === 'history' ? 'bg-slate-800 hover:bg-slate-900 text-white' : ''}`}
            onClick={() => { setActiveTab('history'); onRefreshLogs(); }}
        >
          <History className="mr-2 h-4 w-4" /> Todos os Registros (Log)
        </Button>
      </div>

      {/* Conteúdo Principal da Campanha */}
      <div className="lg:col-span-3">
        
        {/* VIEW 1: DISPARO P/ TURMAS */}
        {activeTab === 'turmas' && (
          <Card className="animate-in fade-in zoom-in-95 duration-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FastForward className="h-5 w-5 text-indigo-500" />
                Criar Campanha para Turma
              </CardTitle>
              <CardDescription>
                Selecione uma turma para disparar mensagens em massa. O Bot adicionará um delay inteligente entre os envios.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
               {!isConnected && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm flex items-center gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      Conecte o número de Whatsapp no Painel antes de Iniciar qualquer campanha.
                  </div>
                )}
              
                <div className="space-y-4">
                  {/* Passo 1 */}
                  <div>
                      <label className="text-sm font-semibold text-slate-700 block mb-1.5 flex justify-between">
                          <span>1. Quem vai receber?</span>
                          <span className="text-xs text-indigo-600 cursor-pointer hover:underline" onClick={onRefreshTurmas}>
                             {loadingTurmas ? 'Atualizando...' : 'Atualizar Listagem'}
                          </span>
                      </label>
                      <Select value={selectedTurmaId} onValueChange={setSelectedTurmaId} disabled={loadingTurmas}>
                        <SelectTrigger className="h-11">
                          <SelectValue placeholder="Selecione a turma..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {turmas.map(t => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.nome} ({t.alunos_com_telefone} celulares cadastrados)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                  </div>

                  {/* Passo 2 */}
                  <div className={`transition-all duration-300 ${!selectedTurma ? 'opacity-30 pointer-events-none' : ''}`}>
                      <label className="text-sm font-semibold text-slate-700 flex justify-between items-center mb-1.5 mt-6">
                        <span>2. Escreva a Mensagem</span>
                        <span className="text-xs text-slate-400 font-normal">Use {"{nome}"} para personalizar</span>
                      </label>
                      <div className="relative">
                          <Textarea 
                             placeholder="Escreva algo brilhante para a turma..."
                             value={campaignMessage}
                             onChange={(e) => setCampaignMessage(e.target.value)}
                             className="h-32 text-sm bg-slate-50 border-slate-200 focus:bg-white resize-none pr-10"
                          />
                          <div className="absolute right-2 top-2">
                             <AiMessageGenerator 
                                 isModal 
                                 triggerButton={
                                     <Button type="button" size="icon" variant="ghost" className="text-indigo-600 hover:bg-indigo-100 rounded-full" title="Melhorar texto com IA" aria-label="Melhorar texto com inteligência artificial">
                                         <Sparkles className="h-4 w-4" />
                                     </Button>
                                 }
                                 onSelectMessage={setCampaignMessage}
                             />
                          </div>
                      </div>
                  </div>
                </div>

                {/* Resumo & Ação */}
                {selectedTurma && !!campaignMessage.trim() && (
                  <div className="mt-6 p-4 bg-indigo-50 border border-indigo-100 rounded-xl space-y-4 animate-in slide-in-from-bottom-2">
                     <p className="text-sm text-indigo-900">
                        Você está prestes a colocar <strong>{selectedTurma.alunos_com_telefone} mensagens</strong> na fila de disparo.
                     </p>

                     {sendProgress && sendProgress.active && (
                       <ProgressMonitor progress={sendProgress} />
                     )}

                     <Button 
                       onClick={() => setConfirmCampaignOpen(true)}
                       disabled={sendingGroup || !isConnected || !campaignMessage.trim()}
                       className="w-full bg-indigo-600 hover:bg-indigo-700 h-11 text-base shadow-sm"
                     >
                        {sendingGroup ? (
                           <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Inserindo na Fila Assíncrona...</>
                        ) : (
                           <><Play className="h-5 w-5 mr-2 fill-white" /> Iniciar Disparo para a Turma</>
                        )}
                     </Button>
                  </div>
                )}
            </CardContent>
          </Card>
        )}

        {/* VIEW 2: DISPARO PARA GRUPOS FAVORITOS */}
        {activeTab === 'groups' && (
          <div className="space-y-6 animate-in fade-in zoom-in-95 duration-200">
            {/* Seção Disparo Rápido */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5 text-amber-500" />
                  Enviar Mensagem ao Grupo
                </CardTitle>
                <CardDescription>
                  Selecione um grupo favorito e envie uma mensagem diretamente.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {favoriteGroups.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm flex items-center gap-3">
                    <Star className="h-5 w-5 text-amber-500" />
                    Nenhum grupo favorito configurado. Use o painel abaixo para selecionar grupos.
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="text-sm font-semibold text-slate-700 block mb-1.5">1. Selecione o Grupo</label>
                      <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                        <SelectTrigger className="h-11">
                          <SelectValue placeholder="Escolha um grupo favorito..." />
                        </SelectTrigger>
                        <SelectContent>
                          {favoriteGroups.map(g => (
                            <SelectItem key={g.id} value={g.id}>
                              ⭐ {g.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className={`transition-all ${!selectedGroup ? 'opacity-30 pointer-events-none' : ''}`}>
                      <label className="text-sm font-semibold text-slate-700 block mb-1.5 mt-4">2. Escreva a Mensagem</label>
                      <Textarea 
                        placeholder="Escreva a mensagem para o grupo..."
                        value={groupMessage}
                        onChange={(e) => setGroupMessage(e.target.value)}
                        className="h-32 text-sm bg-slate-50 border-slate-200 focus:bg-white resize-none"
                      />
                    </div>

                    {selectedGroup && groupMessage.trim() && (
                      <Button 
                        onClick={handleSendToWAGroup} 
                        disabled={sendingToGroup || !isConnected}
                        className="w-full bg-amber-500 hover:bg-amber-600 h-11 text-base text-white shadow-sm"
                      >
                        {sendingToGroup ? (
                          <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Enviando...</>
                        ) : (
                          <><Send className="h-5 w-5 mr-2" /> Enviar para {selectedGroup.name}</>
                        )}
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Seção Gerenciar Favoritos */}
            <BotGroupManager 
              escolaId={escolaId}
              config={config}
              isConnected={isConnected}
              onSaveConfig={onSaveConfig}
              savingConfig={savingConfig}
            />
          </div>
        )}

        {/* VIEW 3: IMPORTAÇÃO EM MASSA */}
        {activeTab === 'import' && (
          <div className="animate-in fade-in zoom-in-95 duration-200">
            <BulkImportTab escolaId="" />
          </div>
        )}

        {/* VIEW 3: HISTÓRICO DE LOGS */}
        {activeTab === 'history' && (
          <Card className="animate-in fade-in zoom-in-95 duration-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="text-lg">Histórico de Disparos</CardTitle>
                <CardDescription>Registro dos últimos envios concluídos pelo bot</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={onRefreshLogs} disabled={loadingLogs}>
                <RefreshCw className={`h-4 w-4 mr-1 ${loadingLogs ? 'animate-spin' : ''}`} />
                Recarregar
              </Button>
            </CardHeader>
            <CardContent>
               {loadingLogs && logs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                     <Loader2 className="h-8 w-8 text-slate-300 animate-spin mb-3" />
                     <p className="text-sm text-slate-500">Buscando o histórico no servidor...</p>
                  </div>
               ) : logs.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                     <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-20" />
                     <p>Nenhuma mensagem foi enviada por este bot ainda.</p>
                  </div>
               ) : (
                  <div className="space-y-4 max-h-[600px] overflow-auto pr-2">
                     {logs.map(log => (
                        <div key={log.id} className="p-3 bg-white border border-slate-100 rounded-lg hover:shadow-sm transition-all text-sm">
                           <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs capitalize font-medium text-slate-600 bg-slate-50">
                                  {log.tipo.replace('_', ' ')}
                                </Badge>
                                <span className="text-slate-500 text-xs">Para: {log.telefone}</span>
                              </div>
                              <span className="text-xs text-slate-400 tabular-nums">
                                {new Date(log.created_at).toLocaleString('pt-BR')}
                              </span>
                           </div>
                           <p className="text-slate-700 whitespace-pre-wrap">{log.mensagem}</p>
                           {log.status === 'falha' && (
                              <div className="mt-2 p-2 bg-red-50 text-red-600 text-xs rounded border border-red-100 flex items-center gap-1.5">
                                 <AlertTriangle className="h-3 w-3 shrink-0" />
                                 Erro detectado. Verifique se o formato do número de celular no cadastro está correto.
                              </div>
                           )}
                           {log.status === 'enviado' && (
                               <div className="mt-2 flex items-center gap-1 text-xs text-green-700 font-medium">
                                   <CheckCircle2 className="h-3 w-3" /> Transmitido com Sucesso
                               </div>
                           )}
                        </div>
                     ))}
                  </div>
               )}
            </CardContent>
          </Card>
        )}

      </div>

      <ConfirmDialog
        open={confirmCampaignOpen}
        onOpenChange={setConfirmCampaignOpen}
        title="Confirmar disparo para a turma"
        description={`Revise antes de enviar: ${selectedTurma?.alunos_com_telefone || 0} mensagens serão colocadas na fila para ${selectedTurma?.nome || 'a turma selecionada'}. Esta ação não poderá ser desfeita após o início.`}
        confirmLabel="Confirmar e iniciar"
        onConfirm={handleStartCampaign}
        loading={sendingGroup}
      />
    </div>
  );
}

// Subcomponente: Monitor de Progresso p/ Envio Massa
function ProgressMonitor({ progress }: { progress: SendProgress }) {
    if (!progress) return null;
    return (
        <div className="mt-4 rounded-lg border border-indigo-100 bg-white p-4" role="status" aria-live="polite">
            <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold text-indigo-700 uppercase">Progresso (Background)</span>
                <span className="text-xs font-semibold text-slate-600">{Math.round(progress.percentComplete)}%</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                <div 
                  className="bg-indigo-500 h-2.5 rounded-full transition-all duration-500" 
                  style={{ width: `${progress.percentComplete}%` }}
                ></div>
            </div>
            <div className="flex justify-between mt-3 text-xs text-slate-500">
                <span>Enviadas: <strong>{progress.sent}</strong> / {progress.total}</span>
                {progress.failed > 0 && <span className="text-red-700 font-medium">Falhas: {progress.failed}</span>}
                <span className="text-slate-600">Restam ~{Math.ceil(progress.estimatedRemainingMs / 1000)}s</span>
            </div>
        </div>
    );
}
